/**
 * DECISIONS 207 (7), the ride-in camera's cabin (the owner: *"always cool to get the shot from inside the dropship so when your
 * player is on the dropship it would be cool to offer a camera view where you're just inside the container seated looking at
 * the rows of soldiers, maybe with some basic red light on then a green light to exit once the doors open"*; and *"make sure
 * that it accurately depicts the number of troops actually in the vehicle"*): the troop container as the rider sees it from
 * a seat — its own copy of the container (dropshipLooks.ts buildTroopContainer), a figure on each seat a rider still aboard
 * holds (ui/dropshipMarks.ts ridersAboard: the sim's count, each seat emptying as its rider's exit passes; the D soldier in
 * 67.63's bench pose, each in its own kit, the squad leader in the leader's), and the jump light over the aisle: red in the
 * flight, green once the ramp opens, the cabin washed in its colour. The rider's own seat is one of them, not drawn: the eye
 * sits in its head (`rideEye`). In the dropship's units (a person 1.8); the host hangs it in the ship's slot in place of the
 * payload and moves the ramp. Presentation only: it reads what it is given.
 *
 * Cost: each figure posed once and baked whole (3 draws, ~2,100 triangles: a still rider needs no moving limbs), the shell 2
 * and the lamp 1 — 36 draws and ~24,000 triangles with all twelve seats full, while the view is on.
 */
import * as THREE from 'three';
import { buildTroopContainer, setContainerRamp, DROPSHIP, type TroopContainer } from './dropshipLooks';
import type { FallShelter } from './weatherView';
import { buildPersonModel, createPersonAnim, updatePersonAnimation, PERSON_UNITS, type PersonKit } from './personModel';
import { bakeStaticParts, disposeObjectTree } from './meshBake';
import type { UnitPalette } from './unitPalette';

/** The look: the lamp's colours, the cabin's own shade (its materials' colour × this: the box shades the sun), its wash in the lamp's colour (an emissive share) and the lamp's own glow, the eye's offset from the head (person units, the seat's frame: up, ahead), how far it turns (radians either way) and the view's field (degrees, vertical: wide enough for the rows across). */
export const RIDE_LOOK = { red: '#ff3a24', green: '#3dff6e', shade: 0.45, wash: 0.07, lamp: 1.6, eye: { up: 0.06, ahead: 0.12 }, yaw: 1.9, pitch: 0.7, fov: 72 };

/** The seated head's height over the seat's floor (person units; 67.63's bench pose). */
const HEAD_Y = 1.17;

/** A rider to seat: its seat (ridersAboard's), and the kit it is drawn in. */
export interface CabinRider { unitId: number; seat: number; kit: PersonKit }

export interface RideCabin {
  root: THREE.Group;
  container: TroopContainer;
  /** each rider's figure (the own one never built), in its seat */
  figures: Map<number, THREE.Object3D>;
  /** the eye: in the own seat's frame (+z across the aisle, y up) */
  eye: THREE.Object3D;
  lamp: THREE.Mesh;
  /** the cabin's own materials, washed in the lamp's colour (each shared one copied once: `copies`) */
  washed: THREE.MeshLambertMaterial[];
  light: 'red' | 'green' | null;
  palette: UnitPalette;
  mine: number;
  copies: Map<THREE.Material, THREE.MeshLambertMaterial>;
}

/** Hidden pieces (a tier not shown, a gun not held) never go into a figure's bake. */
const shown = (o: THREE.Object3D): boolean => { for (let q: THREE.Object3D | null = o; q; q = q.parent) if (!q.visible) return false; return true; };

/** The cabin's own copies of `o`'s materials, so its wash never reaches the world's (a bake's materials are shared). */
function ownMaterials(c: Pick<RideCabin, 'copies' | 'washed' | 'lamp'>, o: THREE.Object3D): void {
  o.traverse(x => {
    const mesh = x as THREE.Mesh;
    if (!mesh.isMesh || mesh === c.lamp || !(mesh.material instanceof THREE.MeshLambertMaterial)) return;
    let m = c.copies.get(mesh.material);
    if (!m) { m = mesh.material.clone(); m.userData.shared = false; m.color.multiplyScalar(RIDE_LOOK.shade); c.copies.set(mesh.material, m); c.washed.push(m); }
    mesh.material = m;
  });
}

/** Seat a rider's figure: 67.63's bench pose in its own kit, baked whole (hidden pieces left out), on its seat. */
function seatFigure(c: RideCabin, r: CabinRider): void {
  const m = buildPersonModel(c.palette, PERSON_UNITS, r.kit), a = createPersonAnim();
  a.state = 'seated'; a.seat = 'bench'; a.hands = {};
  updatePersonAnimation(m, a, 0);
  const hidden: THREE.Object3D[] = [];
  m.root.traverse(o => { if ((o as THREE.Mesh).isMesh && !shown(o)) hidden.push(o); });
  bakeStaticParts(m.root, { exclude: hidden });
  ownMaterials(c, m.root);
  c.container.seats[r.seat].at.add(m.root);
  c.figures.set(r.unitId, m.root);
}

/** Build the cabin for the rider `mine` (a unit id among `riders`), each other rider seated in its kit. */
export function buildRideCabin(p: UnitPalette, riders: readonly CabinRider[], mine: number): RideCabin {
  const container = buildTroopContainer(p), root = container.root;
  const lamp = root.getObjectByName('jumpLight') as THREE.Mesh;
  lamp.material = new THREE.MeshLambertMaterial({ color: RIDE_LOOK.red, emissive: RIDE_LOOK.red, emissiveIntensity: RIDE_LOOK.lamp });
  lamp.userData.noBake = true;
  bakeStaticParts(root, { exclude: [container.ramp, lamp] });
  bakeStaticParts(container.ramp);
  const c: RideCabin = { root, container, figures: new Map(), eye: new THREE.Object3D(), lamp, washed: [], light: null, palette: p, mine, copies: new Map() };
  ownMaterials(c, root);
  // the eye in the own seat's head, looking across the aisle
  c.eye.position.set(0, HEAD_Y + RIDE_LOOK.eye.up, RIDE_LOOK.eye.ahead);
  updateRideCabin(c, riders, 0);
  return c;
}

/**
 * The riders aboard now seated (a figure seated for each newly aboard — 208: they board the parked ship one by one — and
 * moved should its seat change; the rest's seats empty), the own seat holding the eye; the ramp's share open (0 shut … 1
 * down) and the light it sets — `go` false holds it red with the ramp down (63.16: on the pad, open for boarding).
 */
export function updateRideCabin(c: RideCabin, aboard: readonly CabinRider[], ramp: number, go = true): void {
  let added = false;
  const on = new Set<number>();
  for (const r of aboard) {
    if (r.unitId === c.mine) { const seat = c.container.seats[r.seat].at; if (c.eye.parent !== seat) seat.add(c.eye); continue; }
    on.add(r.unitId);
    const f = c.figures.get(r.unitId);
    if (!f) { seatFigure(c, r); added = true; }
    else if (f.parent !== c.container.seats[r.seat].at) c.container.seats[r.seat].at.add(f);
  }
  for (const [id, f] of c.figures) f.visible = on.has(id);
  setContainerRamp(c.container, ramp);
  const light = go && ramp > 0 ? 'green' : 'red';
  if (light === c.light && !added) return;
  c.light = light;
  const col = new THREE.Color(light === 'green' ? RIDE_LOOK.green : RIDE_LOOK.red);
  const lm = c.lamp.material as THREE.MeshLambertMaterial;
  lm.color.copy(col); lm.emissive.copy(col);
  for (const m of c.washed) { m.emissive.copy(col); m.emissiveIntensity = RIDE_LOOK.wash; }
}

/** The figures seated now. */
export function seatedCount(c: RideCabin): number {
  let n = 0;
  for (const f of c.figures.values()) if (f.visible) n++;
  return n;
}

const _q = new THREE.Quaternion(), _s = new THREE.Vector3();
/** The eye in the world and where it looks: across the aisle, turned `yaw` (left +) and `pitch` (up +), each held to RIDE_LOOK's reach. */
export function rideEye(c: RideCabin, yaw: number, pitch: number, pos: THREE.Vector3, look: THREE.Vector3, up: THREE.Vector3): void {
  c.eye.updateWorldMatrix(true, false);
  const y = Math.max(-RIDE_LOOK.yaw, Math.min(RIDE_LOOK.yaw, yaw)), x = Math.max(-RIDE_LOOK.pitch, Math.min(RIDE_LOOK.pitch, pitch));
  c.eye.matrixWorld.decompose(pos, _q, _s);   // (decomposed: in play the ship is scaled to the person, and a scaled matrix is no rotation)
  const q = _q;
  look.set(Math.sin(y) * Math.cos(x), Math.sin(x), Math.cos(y) * Math.cos(x)).applyQuaternion(q).normalize();
  up.set(0, 1, 0).applyQuaternion(q).normalize();
}

/**
 * 207 (7)'s follow-up (the owner: *"it's raining inside"*): the cabin's inside — the container's box, floor to roof, wall to
 * wall, ramp to front — as a shelter from the weather's fall (weatherView.ts FallShelter), where the ship holds it now (its
 * scale to the person taken out: the shelter's frame is unscaled, its half-extents in world units). Written into `out`.
 */
export function rideShelter(c: RideCabin, out: FallShelter): FallShelter {
  const B = DROPSHIP.box;
  c.root.updateWorldMatrix(true, false);
  c.root.matrixWorld.decompose(_at, _q, _s);
  _at.add(_mid.set(0, (B.floor + B.height) / 2 * _s.y, 0).applyQuaternion(_q));
  out.toBox.compose(_at, _q, _one).invert();
  out.half.set((B.width / 2) * _s.x, ((B.height - B.floor) / 2) * _s.y, (B.length / 2) * _s.z);
  return out;
}
const _at = new THREE.Vector3(), _mid = new THREE.Vector3(), _one = new THREE.Vector3(1, 1, 1);

/** Free the cabin: its baked geometry, its own materials and the lamp's (the world's shared assets skipped). */
export function disposeRideCabin(c: RideCabin): void {
  c.root.removeFromParent();
  disposeObjectTree(c.root);
}
